import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  verifyCertificate,
  type CertificateVerification,
} from '../features/landing/certificateVerifyApi';

type Status = 'loading' | 'success' | 'error';

export function VerifyCertificatePage() {
  const { t, i18n } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [certificate, setCertificate] = useState<CertificateVerification | null>(null);

  useEffect(() => {
    if (!code) {
      setStatus('error');
      return;
    }
    verifyCertificate(code)
      .then((data) => {
        setCertificate(data);
        setStatus('success');
      })
      .catch(() => setStatus('error'));
  }, [code]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-paper-100 px-4 font-manuscript">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #8c2d1e 1.5px, transparent 0)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2.5">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full">
            <img src="/logo.png" alt={t('common.appName')} className="h-full w-full object-cover" />
          </span>
          <span className="font-display text-lg font-semibold text-manuscript-ink">{t('common.appName')}</span>
        </Link>

        <div className="rounded-sm border border-manuscript-line-strong bg-paper-50 p-8 text-center">
          {status === 'loading' && (
            <p className="text-sm text-manuscript-ink-soft">{t('common.loading')}</p>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/10 text-2xl text-red-500">
                ✕
              </div>
              <h1 className="mt-4 font-display text-xl font-semibold text-manuscript-ink">
                {t('verifyCertificate.invalidTitle')}
              </h1>
              <p className="mt-3 text-sm text-manuscript-ink-soft">
                {t('verifyCertificate.invalidBody')}
              </p>
            </>
          )}

          {status === 'success' && certificate && (
            <>
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10 text-2xl text-emerald-500">
                ✓
              </div>
              <h1 className="mt-4 font-display text-xl font-semibold text-manuscript-ink">
                {t('verifyCertificate.validTitle')}
              </h1>
              <p className="mt-2 text-sm text-manuscript-ink-soft">
                {t('verifyCertificate.validBody')}
              </p>

              <dl className="mt-6 space-y-3 text-left">
                <div className="border-b border-manuscript-line pb-3">
                  <dt className="text-xs uppercase tracking-wide text-manuscript-ink-faint">
                    {t('verifyCertificate.studentName')}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-manuscript-ink">
                    {certificate.studentName}
                  </dd>
                </div>
                <div className="border-b border-manuscript-line pb-3">
                  <dt className="text-xs uppercase tracking-wide text-manuscript-ink-faint">
                    {t('verifyCertificate.course')}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-manuscript-ink">
                    {certificate.courseName}
                  </dd>
                </div>
                <div className="border-b border-manuscript-line pb-3">
                  <dt className="text-xs uppercase tracking-wide text-manuscript-ink-faint">
                    {t('verifyCertificate.issuedAt')}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-manuscript-ink">
                    {new Date(certificate.issuedAt).toLocaleDateString(i18n.language, {
                      dateStyle: 'long',
                    })}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-manuscript-ink-faint">
                    {t('verifyCertificate.serialNumber')}
                  </dt>
                  <dd className="mt-1 text-sm font-medium text-manuscript-ink">
                    {certificate.serialNumber}
                  </dd>
                </div>
              </dl>
            </>
          )}

          <Link
            to="/login"
            className="mt-6 inline-block text-sm font-medium text-seal-500 hover:text-seal-400"
          >
            {t('verifyCertificate.backHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}
